import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Phone,
  Building2,
  Calendar,
  Clock,
  MessageSquare,
  UserCheck,
  UserX,
  ArrowRightLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { FlowJourneyTimeline } from '@/components/conversations/FlowJourneyTimeline';
import { JourneyTimelineSkeleton } from '@/components/flow-builder/FlowSkeletons';
import type { OperatorConversation } from '@/lib/operator-api/types';

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isMasked(value: unknown): boolean {
  if (!value || typeof value !== 'string') return false;
  return value.includes('***') || value.includes('*');
}

interface ContactPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversation: OperatorConversation;
  operatorId?: string;
  isAssignmentLoading: boolean;
  onClaim: () => void;
  onUnassign: () => void;
  onTransfer: (targetOperatorId: string) => void;
  showTransferDropdown: boolean;
  onToggleTransferDropdown: (show: boolean) => void;
  availableOperators: Array<{ id: string; name: string; email: string; role: string }>;
  // Flow journey
  flowContext: { currentStep: string; flowNameKey: string } | null;
  journeyEvents: any[];
  journeyLoading: boolean;
  journeyError: string | null;
  refetchJourney: () => void;
}

export function ContactPanel({
  open,
  onOpenChange,
  conversation,
  operatorId,
  isAssignmentLoading,
  onClaim,
  onUnassign,
  onTransfer,
  showTransferDropdown,
  onToggleTransferDropdown,
  availableOperators,
  flowContext,
  journeyEvents,
  journeyLoading,
  journeyError,
  refetchJourney,
}: ContactPanelProps) {
  const { t } = useTranslation();

  const initials = conversation.customerName
    ? conversation.customerName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('conversations.info.title')}</SheetTitle>
          <SheetDescription className="sr-only">
            {t('conversations.info.title')}
          </SheetDescription>
        </SheetHeader>

        {/* Customer */}
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary">
              {initials}
            </div>
            <div>
              <p className="font-medium">
                {conversation.customerName
                  ? (isMasked(conversation.customerName)
                      ? <span className="text-amber-700">{conversation.customerName}</span>
                      : conversation.customerName)
                  : <span className="text-muted-foreground">{t('common.notProvided')}</span>
                }
              </p>
              <a
                href={`tel:${conversation.customerPhone}`}
                className="text-sm text-muted-foreground flex items-center gap-1 hover:text-primary transition-colors"
              >
                <Phone className="h-3 w-3" />
                {isMasked(conversation.customerPhone)
                  ? <span className="text-amber-700">{conversation.customerPhone}</span>
                  : conversation.customerPhone
                }
              </a>
            </div>
          </div>

          <Separator />

          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('conversations.info.business')}</span>
              <span className="ml-auto font-medium truncate max-w-[60%] text-right">
                {conversation.businessName || conversation.businessId}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('conversations.info.started')}</span>
              <span className="ml-auto font-medium">{formatDateTime(conversation.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('conversations.info.lastMessage')}</span>
              <span className="ml-auto font-medium">{formatDateTime(conversation.lastMessageAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('conversations.info.messageCount')}</span>
              <span className="ml-auto font-medium">{conversation.messageCount}</span>
            </div>
          </div>

          <Separator />

          {/* Assignment */}
          <div>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <UserCheck className="h-4 w-4 text-primary" />
              {t('conversations.assignment.title')}
            </h3>
            {conversation.assignedOperator ? (
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-sm">{conversation.assignedOperator.name}</p>
                  <p className="text-xs text-muted-foreground">{conversation.assignedOperator.email}</p>
                  {conversation.assignedAt && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('conversations.assignment.since', { date: formatDateTime(conversation.assignedAt) })}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {conversation.assignedOperatorId === operatorId && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={onUnassign}
                      disabled={isAssignmentLoading}
                    >
                      <UserX className="h-3.5 w-3.5 mr-1.5" />
                      {t('conversations.assignment.release')}
                    </Button>
                  )}
                  <div className="relative flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => onToggleTransferDropdown(!showTransferDropdown)}
                      disabled={isAssignmentLoading}
                    >
                      <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                      {t('conversations.assignment.transfer')}
                    </Button>
                    {showTransferDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg z-10 max-h-[200px] overflow-y-auto">
                        {availableOperators.length === 0 ? (
                          <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            {t('conversations.assignment.loadingOperators')}
                          </div>
                        ) : (
                          availableOperators
                            .filter((op) => op.id !== conversation.assignedOperatorId)
                            .map((op) => (
                              <button
                                key={op.id}
                                onClick={() => onTransfer(op.id)}
                                disabled={isAssignmentLoading}
                                className="w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors text-sm disabled:opacity-50"
                              >
                                <p className="font-medium">{op.name}</p>
                                <p className="text-xs text-muted-foreground">{op.email} &middot; {op.role}</p>
                              </button>
                            ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {t('conversations.assignment.unassigned')}
                </p>
                <Button
                  className="w-full"
                  size="sm"
                  onClick={onClaim}
                  disabled={isAssignmentLoading}
                >
                  {isAssignmentLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <UserCheck className="h-4 w-4 mr-2" />
                  )}
                  {t('conversations.assignment.claim')}
                </Button>
              </div>
            )}
          </div>

          {/* Flow Journey */}
          {flowContext && (
            <>
              <Separator />
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-primary" />
                  {t('flowBuilder.journey.title')}
                </h3>
                {journeyLoading ? (
                  <JourneyTimelineSkeleton eventCount={4} />
                ) : journeyError ? (
                  <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                    <AlertCircle className="h-6 w-6 mb-2 text-red-400" />
                    <p className="text-sm">{t('common.error')}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={refetchJourney}
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      {t('common.refresh')}
                    </Button>
                  </div>
                ) : journeyEvents.length > 0 ? (
                  <FlowJourneyTimeline
                    events={journeyEvents}
                    currentStage={flowContext.currentStep}
                    flowName={t(flowContext.flowNameKey)}
                    maxVisibleEvents={10}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
                    <Clock className="h-6 w-6 mb-2 opacity-40" />
                    <p className="text-sm">{t('common.noResults')}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
